const SESSION_KEY = "sessionKey";
const SESSION_DATA = "sessionData";
const ACTIVE_LOGIN_KEY = "activeLoginKey";
const SAVED_ACCOUNTS = "savedAccounts";
const PENDING_OAUTH = "pendingOAuthRequest";
const PENDING_OAUTH_LIFETIME = 60 * 60 * 1000;

function parseJson(value, fallback) {
    if (!value) {
        return fallback;
    }

    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

function normalizeLoginKey(loginKey) {
    return String(loginKey || "").trim();
}

function normalizeSavedAccount(account) {
    const loginKey = normalizeLoginKey(account?.loginKey);
    if (!loginKey) {
        return null;
    }

    return {
        loginKey,
        data: account.data || {},
        lastUsedAt: account.lastUsedAt || 0
    };
}

function writeSavedAccounts(accounts) {
    localStorage.setItem(SAVED_ACCOUNTS, JSON.stringify(accounts));
}

export function getSession(updating = false) {
    var sessionKey = localStorage.getItem(SESSION_KEY);
    var sessionData = localStorage.getItem(SESSION_DATA);

    if (!sessionKey) {
        return {}
    }

    return {
        sessionKey,
        data: parseJson(sessionData, {})
    }
}

export function putSession(session, loginKey) {
    localStorage.setItem(SESSION_KEY, session.sessionKey);
    localStorage.setItem(SESSION_DATA, JSON.stringify(session.data));

    const normalizedLoginKey = normalizeLoginKey(loginKey);
    if (normalizedLoginKey) {
        localStorage.setItem(ACTIVE_LOGIN_KEY, normalizedLoginKey);
    }
}

export function setPendingOAuthRequest(tnonce) {
    localStorage.setItem(PENDING_OAUTH, JSON.stringify({ tnonce, savedAt: Date.now() }));
}

export function clearPendingOAuthRequest() {
    localStorage.removeItem(PENDING_OAUTH);
}

export function getPendingOAuthPath() {
    const pending = parseJson(localStorage.getItem(PENDING_OAUTH), null);
    if (!pending?.tnonce || Date.now() - pending.savedAt > PENDING_OAUTH_LIFETIME) {
        clearPendingOAuthRequest();
        return null;
    }

    return `/oauth/authorize?${new URLSearchParams({ tnonce: pending.tnonce })}`;
}

export function isAuthed() {
    return !!localStorage.getItem(SESSION_KEY);
}

export function clearCurrentSession() {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_DATA);
    localStorage.removeItem(ACTIVE_LOGIN_KEY);
    delete window.authenticationDetails;
}

export function getCurrentLoginKey() {
    return localStorage.getItem(ACTIVE_LOGIN_KEY);
}

export function getSavedAccounts() {
    const accounts = parseJson(localStorage.getItem(SAVED_ACCOUNTS), []);
    if (!Array.isArray(accounts)) {
        return [];
    }

    return accounts
        .map(normalizeSavedAccount)
        .filter(Boolean)
        .sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

export function saveAuthenticatedAccount(loginKey, accountData) {
    const normalizedLoginKey = normalizeLoginKey(loginKey);
    if (!normalizedLoginKey) {
        return;
    }

    const accounts = getSavedAccounts();
    const existingIndex = accounts.findIndex(account => account.loginKey === normalizedLoginKey);
    const savedAccount = {
        loginKey: normalizedLoginKey,
        data: accountData || {},
        lastUsedAt: Date.now()
    };

    if (existingIndex >= 0) {
        accounts[existingIndex] = savedAccount;
    } else {
        accounts.push(savedAccount);
    }

    writeSavedAccounts(accounts);
    localStorage.setItem(ACTIVE_LOGIN_KEY, normalizedLoginKey);
}

export function removeSavedAccount(loginKey) {
    const normalizedLoginKey = normalizeLoginKey(loginKey);
    if (!normalizedLoginKey) {
        return;
    }

    writeSavedAccounts(getSavedAccounts().filter(account => account.loginKey !== normalizedLoginKey));
}

export function removeCurrentSavedAccount() {
    const loginKey = getCurrentLoginKey();
    if (loginKey) {
        removeSavedAccount(loginKey);
    }

    clearCurrentSession();
}

export function getNextSavedAccount() {
    const currentLoginKey = getCurrentLoginKey();
    return getSavedAccounts().find(account => account.loginKey !== currentLoginKey);
}
